import { environment } from "../environments/environment";
import { USER_TOKEN_KEY_NAME } from "../constant/constats";

const uploadUri = `${environment.uploadUrl}`;

const buildHeaders = (token: string | null, isJson: boolean = false): Record<string, string> => {
  const headers: Record<string, string> = {};
  if (isJson) {
    headers["Content-Type"] = "application/json";
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

/**
 * High-speed direct S3 upload with automatic fallback to server proxy upload.
 * Preserves exact return format: { success: true, message: string, files: Array<{ fieldname, originalname, mimetype, destination, filename, path, size }> }
 */
export const uploadFile = async (files: File | any, token: string = "", isGuestUpload: boolean = false) => {
  const authToken =
    !isGuestUpload && typeof window !== "undefined" && window.localStorage
      ? window.localStorage.getItem(USER_TOKEN_KEY_NAME)
      : null;

  const isGuest = isGuestUpload || (!authToken && !token);
  const bearerToken = isGuest ? null : authToken || token;

  // Extract files from FormData or single File
  const extractedFiles: { fieldname: string; file: File }[] = [];
  let uploadType: string | undefined = undefined;

  if (typeof FormData !== "undefined" && files instanceof FormData) {
    files.forEach((value: FormDataEntryValue, key: string) => {
      if (value instanceof File) {
        extractedFiles.push({ fieldname: key, file: value });
      } else if (key === "type" && typeof value === "string") {
        uploadType = value;
      }
    });
  } else if (typeof File !== "undefined" && files instanceof File) {
    extractedFiles.push({ fieldname: "images", file: files });
  }

  // 🚀 Attempt Direct-to-S3 Upload for maximum speed
  if (extractedFiles.length > 0) {
    try {
      // 1. Get presigned batch URLs from backend (~15ms)
      const presignResponse = await fetch(`${uploadUri}presigned-upload-batch`, {
        method: "POST",
        headers: buildHeaders(bearerToken, true),
        body: JSON.stringify({
          files: extractedFiles.map(({ fieldname, file }) => ({
            fieldname,
            originalname: file.name,
            mimetype: file.type || "application/octet-stream",
            size: file.size,
          })),
          type: uploadType,
        }),
      });

      if (presignResponse.ok) {
        const presignData = await presignResponse.json();

        if (presignData.success && Array.isArray(presignData.files)) {
          // 2. Upload files in parallel directly to S3 (bypassing EC2 completely!)
          await Promise.all(
            presignData.files.map(async (item: any, index: number) => {
              const rawFile = extractedFiles[index].file;
              const s3UploadRes = await fetch(item.uploadUrl, {
                method: "PUT",
                headers: {
                  "Content-Type": item.mimetype,
                },
                body: rawFile,
              });

              if (!s3UploadRes.ok) {
                throw new Error(`S3 direct upload failed for ${rawFile.name}: ${s3UploadRes.statusText}`);
              }
            })
          );

          // 3. Notify backend to register files and trigger background variants
          const completeResponse = await fetch(`${uploadUri}presigned-upload-complete`, {
            method: "POST",
            headers: buildHeaders(bearerToken, true),
            body: JSON.stringify({ files: presignData.files }),
          });

          if (completeResponse.ok) {
            const completeData = await completeResponse.json();
            return completeData;
          }
        }
      }
    } catch (directUploadErr) {
      console.warn("Direct S3 upload encountered error, falling back to server upload:", directUploadErr);
      // Seamlessly fall through to legacy upload below
    }
  }

  // 🔄 Fallback: Legacy upload (Mobile app & S3 fallback flow)
  try {
    const endpoint = isGuest ? "upload-guest" : "upload";
    const response = await fetch(`${uploadUri}${endpoint}`, {
      method: "POST",
      body: files,
      headers: buildHeaders(bearerToken, false),
    });

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(`${errorResponse.message}`);
    }

    const result = await response.json();
    return result;
  } catch (error: any) {
    console.error("Error posting form data:", error);
    throw error;
  }
};

export interface UploadProgressCallback {
  (progressPercent: number, loaded: number, total: number): void;
}

export interface UploadingFileItem {
  id: string;
  file: File;
  previewUrl: string;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  errorMessage?: string;
}

export const uploadSingleFileWithProgress = async (
  file: File,
  token: string = "",
  onProgress?: UploadProgressCallback,
  isGuestUpload: boolean = false
): Promise<any> => {
  const authToken =
    !isGuestUpload && typeof window !== "undefined" && window.localStorage
      ? window.localStorage.getItem(USER_TOKEN_KEY_NAME)
      : null;

  const isGuest = isGuestUpload || (!authToken && !token);
  const bearerToken = isGuest ? null : authToken || token;

  // 🚀 Attempt Direct-to-S3 with real-time XHR progress
  try {
    const presignResponse = await fetch(`${uploadUri}presigned-upload-batch`, {
      method: "POST",
      headers: buildHeaders(bearerToken, true),
      body: JSON.stringify({
        files: [
          {
            fieldname: "images",
            originalname: file.name,
            mimetype: file.type || "application/octet-stream",
            size: file.size,
          },
        ],
      }),
    });

    if (presignResponse.ok) {
      const presignData = await presignResponse.json();
      if (presignData.success && presignData.files?.[0]?.uploadUrl) {
        const item = presignData.files[0];

        // Direct upload to S3 via XHR with progress
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", item.uploadUrl);
          xhr.setRequestHeader("Content-Type", item.mimetype);

          if (xhr.upload && onProgress) {
            xhr.upload.onprogress = (event: ProgressEvent) => {
              if (event.lengthComputable) {
                const percent = Math.round((event.loaded / event.total) * 100);
                onProgress(percent, event.loaded, event.total);
              }
            };
          }

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(`S3 direct upload failed with status ${xhr.status}`));
          };
          xhr.onerror = () => reject(new Error("Network error during S3 upload"));
          xhr.send(file);
        });

        // Notify backend of completion
        const completeResponse = await fetch(`${uploadUri}presigned-upload-complete`, {
          method: "POST",
          headers: buildHeaders(bearerToken, true),
          body: JSON.stringify({ files: presignData.files }),
        });

        if (completeResponse.ok) {
          return await completeResponse.json();
        }
      }
    }
  } catch (directErr) {
    console.warn("Direct upload with progress failed, using fallback:", directErr);
  }

  // 🔄 Fallback: Legacy upload with progress
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const endpoint = isGuest ? "upload-guest" : "upload";
    const url = `${uploadUri}${endpoint}`;

    xhr.open("POST", url);

    if (bearerToken) {
      xhr.setRequestHeader("Authorization", `Bearer ${bearerToken}`);
    }

    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (event: ProgressEvent) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent, event.loaded, event.total);
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const result = JSON.parse(xhr.responseText);
          resolve(result);
        } catch {
          reject(new Error("Invalid response format from upload service"));
        }
      } else {
        try {
          const errorResponse = JSON.parse(xhr.responseText);
          reject(new Error(errorResponse.message || `Upload failed with status ${xhr.status}`));
        } catch {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      }
    };

    xhr.onerror = () => reject(new Error("Network error during file upload"));
    xhr.ontimeout = () => reject(new Error("File upload timed out"));

    const formData = new FormData();
    formData.append("images", file);
    xhr.send(formData);
  });
};

export const poductLogCount = async (
  productId: number,
  userId: number | null
) => {
  try {
    const response = await fetch(`${uploadUri}createProductLog`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ productId, userId }),
    });
    if (!response.ok) {
      console.log(`Response not okay: ${response}`);
    }
    return;
  } catch (error: any) {
    console.error(`Error creating log: ${error}`);
    return;
  }
};
